import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom"; // For portals (context menu)
import "./Tool.css";
import { Progress } from "../components/ui/Progress";
import { IoInformationCircleOutline } from "react-icons/io5";
import { useNavigate } from 'react-router-dom';

//API end points
import { API_ENDPOINTS } from '../config/api';
import { authFetch } from '../utils/authFetch';

// Helper function to format seconds into human-readable time.

const formatRuntime = (totalSeconds) => {
  totalSeconds = Number(totalSeconds);
  if (totalSeconds < 60) {
    return `${totalSeconds.toFixed(2)} seconds`;
  } else if (totalSeconds < 3600) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}${seconds > 0 ? ` ${seconds} second${seconds !== 1 ? 's' : ''}` : ''}`;
  } else if (totalSeconds < 86400) {
    const hours = Math.floor(totalSeconds / 3600);
    const remainder = totalSeconds % 3600;
    const minutes = Math.floor(remainder / 60);
    return `${hours} hour${hours !== 1 ? 's' : ''}${minutes > 0 ? ` ${minutes} minute${minutes !== 1 ? 's' : ''}` : ''}`;
  } else {
    const days = Math.floor(totalSeconds / 86400);
    const remainder = totalSeconds % 86400;
    const hours = Math.floor(remainder / 3600);
    return `${days} day${days !== 1 ? 's' : ''}${hours > 0 ? ` ${hours} hour${hours !== 1 ? 's' : ''}` : ''}`;
  }
};

function Tool({
  url,
  setUrl,
  keyword,
  setKeyword,
  handleStoreUrl,
  storedResults = [],
  setStoredResults,
  handleDelete,
  loading,
  setLoading,
  userPlan
}) {
  const navigate = useNavigate();
  const [keywords, setKeywords] = useState([]);
  const [results, setResults] = useState({ company: '', keywords: [], runtime: '',url: '' });
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');
  const [controller, setController] = useState(null);
  const [lockModalVisible, setLockModalVisible] = useState(false);
  const hourlyLocked = userPlan !== 'Active Job Seeker/ Hourly Search Plan';
 
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalUrl, setModalUrl] = useState('');
  const [keywordProgress, setKeywordProgress] = useState({});
  const [selectedFrequency, setSelectedFrequency] = useState("Daily");
  const [errors, setErrors] = useState({});
  const [fallbackUrl, setFallbackUrl] = useState('');

  // ------------------- NEW: Context Menu State (like Dashboard) -------------------
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    searchId: null,
    keywordIndex: null,
  });

  useEffect(() => {
    const savedUrl = localStorage.getItem('pendingUrl');
    const savedKeywordsList = localStorage.getItem('pendingKeywordsList');
    const savedKeyword = localStorage.getItem('pendingKeyword'); 
  
    if (savedUrl) {
      setUrl(savedUrl);
    }
  
    if (savedKeywordsList) {
      try {
        const parsedKeywords = JSON.parse(savedKeywordsList);
        if (Array.isArray(parsedKeywords)) {
          const unique = [...new Set(parsedKeywords.map(k => k.trim()).filter(Boolean))];
          setKeywords(unique);
        }
      } catch (e) {
        console.warn("Failed to parse pendingKeywordsList");
      }
    }
  
    if (savedKeyword) {
      setKeyword(savedKeyword);
    }
  }, []);
  
  useEffect(() => {
    const shouldTrigger = localStorage.getItem("triggerSearchAfterLogin") === "true";
    if (shouldTrigger && keywords.length > 0) {
      localStorage.removeItem("triggerSearchAfterLogin");
      handleSearch();
    }
  }, [keywords]);
  
  
  
  // Hide context menu when clicking anywhere else
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) {
        setContextMenu({
          visible: false,
          x: 0,
          y: 0,
          searchId: null,
          keywordIndex: null,
        });
      }
    };
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, [contextMenu.visible]);
  // ------------------------------------------------------------------------------

  // MODAL control
  const showModal = (message, fallbackUrl = '') => {
    setModalMessage(message);
    setModalUrl(fallbackUrl);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalMessage('');
    setModalVisible(false);
  };

  const handleSearchManually = () => {
    if (modalUrl) {
      window.open(modalUrl, '_blank');
    }
    closeModal();
  };
  const handleOk = () => {
    if (modalUrl) {
      navigate(modalUrl);
    }
    closeModal();
  };


  const cacheForms = () => {
    localStorage.setItem('pendingUrl', url);
    localStorage.setItem('pendingKeywordsList', JSON.stringify(keywords));
    localStorage.setItem('pendingKeyword', keyword); 
  };
  

  const handleFrequencyChange = (event) => {
    setSelectedFrequency(event.target.value);
  };
  const [quotaNotificationSent, setQuotaNotificationSent] = useState(false);
  const handleKeywordAdd = async (e) => {
    if (e.key === 'Enter' && keyword.trim()) {
      const userId = localStorage.getItem('userEmail') || 'defaultUserId';
      let quotaExceeded = false;
  
      try {
        const response = await authFetch(
          `${API_ENDPOINTS.GET_USER_INFO}?user_id=${encodeURIComponent(userId)}`
        );
        if (response.ok) {
          let data = await response.json();
          let rawBody = data.body ?? JSON.stringify(data);
          rawBody = rawBody.replace(/^"|"$/g, '');
          rawBody = rawBody.replace(/User information:\s*/, '');
          rawBody = rawBody.replace(/'/g, '"');
          rawBody = rawBody.replace(/Decimal\("(\d+)"\)/g, '$1');
          const userInfo = JSON.parse(rawBody).Item;
  
          const currentCount = Number(userInfo.CurrentSearchCount) || 0;
          const maxSearches = Number(userInfo.MaxSearches) || 0;
          const remainingQuota = maxSearches - currentCount;
  
          if (keywords.length >= remainingQuota) {
            quotaExceeded = true;
            showModal(
              "You've reached your keyword quota for the Basic plan. Upgrade to continue searching.",
              "/pricing"
            );
            
            if (url && url.trim()) {
              const companyName = extractCompanyName(url);
              const newKeyword = keyword.trim();
              
              const searchResults = [{
                keyword: newKeyword,
                found: "not-found",
                url: null,
                match_type: "not-found",
                correct_status: "pending"
              }];
              
              const newEntry = {
                company_name: companyName,
                url: url.trim() || "https://placeholder.com",
                keywords: searchResults,
                runtime: "0.00 seconds",
                frequency: selectedFrequency,
                search_id: `id-${Date.now()}-${Math.floor(Math.random()*10000)}`,
                timeFormatted: new Date().toLocaleString(),
                timeRaw: Date.now()
              };
              
              await handleStoreUrl(newEntry);
            }
            return;
          }
        }
      } catch (err) {
        console.error("Quota check failed during keyword add:", err);
      }
  
      const newKeyword = keyword.trim();
      setKeywords([...keywords, newKeyword]);
      setKeywordProgress((prev) => ({ ...prev, [newKeyword]: 0 }));
      setKeyword('');
    }
  };
  

  const handleRemoveKeyword = (index) => {
    setKeywords(keywords.filter((_, i) => i !== index));
    setKeywordProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress[keywords[index]];
      return newProgress;
    });
  };

  // URL validator
  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Main "Search" function
  const handleSearch = async () => {
    let quotaExceeded = false;
    // 1) If the user is not logged in, redirect to signup
    if (localStorage.getItem('isLoggedIn') !== 'true') {
      const cleaned = keyword.trim();
      const combinedKeywords = cleaned ? [...keywords, cleaned] : keywords;
      
      localStorage.setItem('pendingUrl', url);
      localStorage.setItem('pendingKeywordsList', JSON.stringify([...new Set(combinedKeywords)]));
      navigate('/signup');
      return;
    }

    // Convert keyword input into a pill before continuing
    let updatedKeywords = [...keywords];
    if (!quotaExceeded) {
      const trimmed = keyword.trim();
      if (trimmed && !updatedKeywords.includes(trimmed)) {
        updatedKeywords.push(trimmed);
        setKeywords(updatedKeywords);
        setKeyword('');
      }
    }

    // 2) QUOTA CHECK: fetch user info and use CurrentSearchCount
    const userId = localStorage.getItem('userEmail') || 'defaultUserId';

    try {
      const userInfoResponse = await authFetch(
        `${API_ENDPOINTS.GET_USER_INFO}`
      );
      if (userInfoResponse.ok) {
        let data = await userInfoResponse.json();
        let rawBody = data.body ?? JSON.stringify(data);
        rawBody = rawBody.replace(/^"|"$/g, '');
        rawBody = rawBody.replace(/User information:\s*/, '');
        rawBody = rawBody.replace(/'/g, '"');
        rawBody = rawBody.replace(/Decimal\("(\d+)"\)/g, '$1');
        const userInfo = JSON.parse(rawBody).Item;

        const currentCount = Number(userInfo.CurrentSearchCount) || 0;
        const maxSearches  = Number(userInfo.MaxSearches)       || 0;

        if (currentCount >= maxSearches) {
          quotaExceeded = true;
          showModal(
            "You've reached the maximum limit for the Basic plan. Please upgrade to Pro for unlimited job searches.",
            "/pricing"
          );
          return; 
        }
      } else {
        console.error("Error fetching user info:", userInfoResponse.status);
      }
    } catch (err) {
      console.error("Error during quota check:", err);
    }

    // Input Validation
    const newErrors = {};
    if (!url) newErrors.url = 'Field is required';
    if (updatedKeywords.length === 0) newErrors.keyword = 'Field is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Extract company, build keyword list
    const companyName = extractCompanyName(url);
    const searchKeywords = [...updatedKeywords];
    
    if (!searchKeywords.length) {
      alert("Please enter at least one keyword.");
      return;
    }

    // Reset UI state
    setResults({ company: "", keywords: [], runtime: "", url: '' });
    setLoading(true);
    setKeywordProgress(searchKeywords.reduce((a, k) => ({...a, [k]:0}), {}));
    const abortController = new AbortController();
    setController(abortController);

    const searchResults = [];
    const startTime = performance.now();
    try {
      if (quotaExceeded) {
        for (const kw of searchKeywords) {
          searchResults.push({ keyword: kw, found: "not-found", url: null });
          updateKeywordProgress(kw, 100);
        }
      } else {
        // Regular search process when quota is not exceeded
        for (const kw of searchKeywords) {
          setCurrentSearchTerm(kw);
          updateKeywordProgress(kw, 0);

          // simulate interim progress
          let prog = 0;
          const iv = setInterval(() => {
            if (prog < 80) updateKeywordProgress(kw, (prog += 20));
          }, 500);

          // ✅ FIXED: Correct payload per YAML - only url, keyword, company
          const resp = await authFetch(API_ENDPOINTS.SEARCH, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            signal: abortController.signal,
            body: JSON.stringify({
              url: url.trim(),
              keyword: kw.trim(),
              company: companyName
            })
          });
          clearInterval(iv);
          
          if (resp.status === 404) {
            searchResults.push({ keyword: kw, found: "not-found", url: null });
            updateKeywordProgress(kw, 100);
            continue;
          }
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

          const message = await resp.text();

          const msg = (message || "").toLowerCase();
          let status = "not-found", matchUrl = null;
          if (msg.includes("successfully found")) {
            status = "exact";
            matchUrl = message.match(/\((.*?)\)/)?.[1] || null;
          } else if (msg.includes("partially found")) {
            status = "partial";
            matchUrl = message.match(/\((.*?)\)/)?.[1] || null;
          }
          searchResults.push({ keyword: kw, found: status, url: matchUrl });
          updateKeywordProgress(kw, 100);
        }
      }

      // finalize runtime & state
      const endTime = performance.now();
      const runtimeStr = `${((endTime - startTime)/1000).toFixed(2)} seconds`;
      setResults({ company: companyName, keywords: searchResults, runtime: runtimeStr, url: url.trim() });

      // build and store the entry
      const newEntry = {
        company_name: companyName,
        url: url.trim() || "https://placeholder.com",
        keywords: searchResults.map(kw => ({
          keyword: kw.keyword,
          url:      kw.url || url.trim(),
          found:    kw.found,
          match_type:
            kw.found === "exact"  ? "exact"  :
            kw.found === "partial"? "partial": "not-found",
          correct_status: "pending"
        })),
        runtime:     runtimeStr,
        frequency:   selectedFrequency === "Once" ? "None" : selectedFrequency,  
        search_id:   `id-${Date.now()}-${Math.floor(Math.random()*10000)}`,
        timeFormatted: new Date().toLocaleString()
      };
      
      if (!quotaExceeded) {
        await handleStoreUrl(newEntry);
      }

    } catch (err) {
      console.error("Error during search:", err);
      alert(`Search failed: ${err.message}`);
      setResults({
        company: companyName,
        keywords: searchKeywords.map(k => ({ keyword:k, found:"not-found", url:null })),
        runtime: "Error",
        url: url.trim()
      });
    } finally {
      setLoading(false);
      setCurrentSearchTerm("");
      setKeyword("");
      setController(null);
      setUrl("");
      setKeywords([]);
    }
  };

  // Cancel search
  const cancelSearch = () => {
    if (controller) {
      controller.abort();
      setLoading(false);
      setController(null);
    }
  };

  // If you still have a handleDelete function:
  const handleDeleteWithNavigation = (index) => {
    if (!handleDelete) return;
    handleDelete(index);
  };

  // Helper: Extract company name
  function extractCompanyName(urlString) {
    try {
      const { hostname } = new URL(urlString);
      // If the hostname contains "myworkdayjobs.com", return the first segment
      if (hostname.includes("myworkdayjobs.com")) {
        return hostname.split('.')[0];
      }
      const parts = hostname.split('.');
      if (parts.length >= 2) {
        return parts[parts.length - 2];
      }
      return parts[0] || 'Unknown Company';
    } catch (error) {
      return 'Unknown Company';
    }
  }

  // Update progress for each keyword
  const updateKeywordProgress = (kw, percentage) => {
    setKeywordProgress((prev) => ({
      ...prev,
      [kw]: percentage,
    }));
  };

  // ----------------------------------------------------------
  // Right-click handlers for keywords
  // ----------------------------------------------------------
  const handleKeywordContextMenu = (e, record, keywordIndex) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      recordId: record.search_id,
      keywordIndex: keywordIndex,
    });
  };

  const CompanyLogo = ({ result }) => {
    const [imgError, setImgError] = useState(false);
    const hostname = (() => { try { return result.url ? new URL(result.url).hostname : null; } catch { return null; } })();
    const companyName = result.company_name || "Unknown Company";
    const formattedCompanyName = companyName.charAt(0).toUpperCase() + companyName.slice(1);
  
    if (hostname && !imgError) {
      return (
        <div className="tooltip-wrapper">
          <img
            src={`https://logo.clearbit.com/${hostname}`}
            onError={() => setImgError(true)}
            alt={`${formattedCompanyName} Logo`}
            style={{
              width: "40px",
              height: "40px",
              objectFit: "contain",
              padding: "4px",
              border: "1px solid black",
              borderRadius: "8px"
            }}
          />
          <span className="tooltip-text">{formattedCompanyName}</span>
        </div>
      );
    }
    return <span>{formattedCompanyName}</span>;
  };
  
  // Mark as applied
  const markAsApplied = () => {
    const { recordId, keywordIndex } = contextMenu;
    setStoredResults((prev) =>
      prev.map((record) => {
        if (record.search_id === recordId) {
          const updatedKeywords = record.keywords.map((kw, idx) =>
            idx === keywordIndex ? { ...kw, status: "applied" } : kw
          );
          return { ...record, keywords: updatedKeywords };
        }
        return record;
      })
    );
    setContextMenu({ visible: false, x: 0, y: 0, recordId: null, keywordIndex: null });
  };

  // Mark as incorrect result
  const markAsIncorrect = () => {
    const { recordId, keywordIndex } = contextMenu;
    setStoredResults((prev) =>
      prev.map((record) => {
        if (record.search_id === recordId) {
          const updatedKeywords = record.keywords.map((kw, idx) =>
            idx === keywordIndex ? { ...kw, status: "incorrect" } : kw
          );
          return { ...record, keywords: updatedKeywords };
        }
        return record;
      })
    );
    setContextMenu({ visible: false, x: 0, y: 0, recordId: null, keywordIndex: null });
  };

  // Mark as correct result
  const markAsCorrect = () => {
    const { recordId, keywordIndex } = contextMenu;
    setStoredResults((prev) =>
      prev.map((record) => {
        if (record.search_id === recordId) {
          const updatedKeywords = record.keywords.map((kw, idx) =>
            idx === keywordIndex ? { ...kw, status: "correct" } : kw
          );
          return { ...record, keywords: updatedKeywords };
        }
        return record;
      })
    );
    setContextMenu({ visible: false, x: 0, y: 0, recordId: null, keywordIndex: null });
  };

  // ----------------------------------------------------------
  // Sort stored results in descending order by timeRaw
  const sortedStoredResults = [...storedResults].sort((a, b) => b.timeRaw - a.timeRaw);

  return (
    <div className="tool-container">
      <div className="tool-header" >
        <h2 className="tool-heading"> Find your dream job</h2>
        { loading && (
          <button className="cancel-button" onClick={cancelSearch}>
            Cancel Search
          </button>
        )}
      </div>

      {/* SEARCH BOX */}
      <div className="search-box">
        {/* URL Input */}
        <div className="input-group">
          <label htmlFor="url" style={{ display: 'flex', alignItems: 'center' }}>
            Career Page URL:
            <div
              className="tooltip"
              data-tooltip="Make sure there is a search box on the career page you enter."
              style={{ marginLeft: '6px' }}
            >
              <IoInformationCircleOutline style={{ cursor: 'pointer' }} />
            </div>
          </label>

          <input
            id="url"
            type="text"
            placeholder="Enter careers page URL here"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setErrors((prev) => ({ ...prev, url: '' }));
            }}
            className={errors.url ? 'error' : ''}
          />
          {errors.url && <div className="error-message">{errors.url}</div>}
        </div>

        {/* Keyword Input */}
        <div className="input-group keyword-input">
          <label htmlFor="keyword" style={{ display: 'flex', alignItems: 'center' }}>
            Job title:
            <div
              className="tooltip"
              data-tooltip="Press 'Enter' each time you add a new job title."
              style={{ marginLeft: '6px' }}
            >
              <IoInformationCircleOutline style={{ cursor: 'pointer' }} />
            </div>
          </label>

          <input
            id="keyword"
            type="text"
            placeholder="Enter job title and press enter"
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setErrors((prev) => ({ ...prev, keyword: '' }));
            }}
            onKeyDown={handleKeywordAdd}
            className={errors.keyword ? 'error' : ''}
          />
          {errors.keyword && <div className="error-message">{errors.keyword}</div>}

          <div className="keywords-container">
            {keywords.map((k, idx) => (
              <div
                key={idx}
                className="keyword-pill"
                style={{
                  position: "relative",
                  background:
                    keywordProgress[k] !== undefined
                      ? `linear-gradient(to right, #c7c7c7 ${keywordProgress[k]}%, #ffffff ${keywordProgress[k]}%)`
                      : "#ffffff",
                }}
              >
                <span className="keyword-text" style={{ position: "relative", zIndex: 1 }}>
                  {k}
                </span>
                {!loading && (
                  <button
                    onClick={() => handleRemoveKeyword(idx)}
                    className="remove-pill-button"
                    style={{ position: "relative", zIndex: 1, marginLeft: "8px" }}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Frequency Radio */}
        <div className="input-group">
          <label>Search Frequency:</label>
          <div className="frequency-options">
            {["Once", "Hourly", "Daily"].map((freq) => {
              const locked = freq === "Hourly" && hourlyLocked;
              return (
                <label
                  key={freq}
                  className="frequency-label"
                  onClick={(e) => {
                    e.preventDefault();
                    if (locked) {
                      setLockModalVisible(true);
                    } else {
                      setSelectedFrequency(freq);
                    }
                  }}
                >
                  <input
                    type="radio"
                    name="searchFrequency"
                    value={freq}
                    checked={selectedFrequency === freq}
                    readOnly
                  />
                  {locked ? "🔒 " : ""}{freq}
                </label>
              );
            })}
          </div>
        </div>

        <div className="search-button-container">
          <button className="search-button" onClick={handleSearch} disabled={loading}>
            Search
          </button>
          {loading && currentSearchTerm && (
            <div className="loading-message">
              <span>Searching for "{currentSearchTerm}"...</span>
            </div>
          )}
        </div>
      </div>

     {/* RESULTS BOX */}
      <div className="results-container">
        <h4>Results:</h4>

        {results.keywords.length > 0 && (
          <div className="stored-result-box">
            <div className="stored-result-content">

              {/* Company Logo Column */}
              <div
                className="company-col"
                style={{
                  display:        "flex",
                  alignItems:     "center",
                  justifyContent: "center",
                }}
              >
                {(() => {
                  let host;
                  try {
                    host = new URL(results.url).hostname;
                  } catch {
                    host = null;
                  }
                  const companyName = results.company || "Unknown Company";
                  const formattedCompanyName = companyName.charAt(0).toUpperCase() + companyName.slice(1);
                  
                  return host ? (
                    <div className="tooltip-wrapper">
                      <img
                        src={`https://logo.clearbit.com/${host}`}
                        alt={`${formattedCompanyName} logo`}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = "none";
                        }}
                        style={{
                          width:        40,
                          height:       40,
                          objectFit:    "contain",
                          borderRadius: 4,
                        }}
                      />
                      <span className="tooltip-text">{formattedCompanyName}</span>
                    </div>
                  ) : (
                    <span>{formattedCompanyName}</span>
                  );
                })()}
              </div>

              {/* Time Column */}
              <div className="time-col">
                <strong>Time:</strong>
                <div>
                  {(() => {
                    const [datePart, timePart] = new Date().toLocaleString().split(",");
                    return timePart ? (
                      <>
                        {datePart.trim()}<br />
                        {timePart.trim()}
                      </>
                    ) : (
                      new Date().toLocaleString()
                    );
                  })()}
                </div>
              </div>

              {/* Keywords Column */}
              <div className="keywords-col">
                <div className="keyword-pills">
                  {results.keywords.map((kw, i) => (
                    <span
                      key={i}
                      className={`keyword-pill ${
                        kw.found === "exact"
                          ? "green-pill"
                          : kw.found === "partial"
                          ? "orange-pill"
                          : "red-pill"
                      }`}
                      onClick={() => {
                        if ((kw.found === "exact" || kw.found === "partial") && kw.url) {
                          window.open(kw.url, "_blank");
                        } else {
                          showModal(
                            `No match found for "${kw.keyword}".`,
                            results.url
                          );
                        }
                      }}
                      style={{
                        cursor:
                          kw.found === "exact" || kw.found === "partial"
                            ? "pointer"
                            : "default",
                      }}
                    >
                      {kw.keyword}
                    </span>
                  ))}
                </div>
              </div>

              {/* Time Saved Column */}
              <div className="runtime-col">
                <strong>Time Saved:</strong>
                <div>{formatRuntime(parseFloat(results.runtime) * 15)}</div>
              </div>

            </div>
          </div>
        )}
      </div>

      {/* MODAL */}
      {modalVisible && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close-icon" onClick={closeModal}>
              ×
            </button>
            <p className="modal-message">{modalMessage}</p>
            {modalUrl === "/pricing" ? (
              <button className="modal-search-button" onClick={handleOk}>
                Upgrade
              </button>
            ) : (
              <button className="modal-search-button" onClick={handleSearchManually}>
                Search Manually
              </button>
            )}
          </div>
        </div>
      )}

      {/* 🔒 Subscription-lock modal */}
      {lockModalVisible && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button
              className="modal-close-icon"
              onClick={() => { setLockModalVisible(false)}} 
            >
              ×
            </button>
            <p>
              Please subscribe to the <strong>Active Job Seeker / Hourly Search Plan</strong> to unlock hourly frequency.
            </p>
            <button
              className="modal-search-button"
              onClick={() => {
                cacheForms() ;
                setLockModalVisible(false);
                window.location.href = '/pricing';
              }}
            >
              View Plans
            </button>
          </div>
        </div>
      )}

      {/* STORED RESULTS (Latest 3) */}
      <div className="stored-urls">
        <h4>Search History:</h4>
        {sortedStoredResults.length > 0 ? (
          sortedStoredResults
            .filter(result => result.keywords && result.keywords.length > 0)
            .slice(0, 3)
            .map((result, index) => {
              const [datePart, timePart] = result.timeFormatted.split(",");
              const timeSaved = parseFloat(result.runtime) * 15;
              return (
                <div key={result.search_id} className="stored-result-box">
                  <div className="stored-result-content">
                    {/* Company Column */}
                    <div className="company-col" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CompanyLogo result={result} />
                    </div>
                    {/* Time Column */}
                    <div className="time-col">
                      <strong>Time:</strong>
                      <div>
                        {timePart ? (
                          <>
                            {datePart.trim()}
                            <br />
                            {timePart.trim()}
                          </>
                        ) : (
                          result.timeFormatted
                        )}
                      </div>
                    </div>
                    {/* Keywords Column */}
                    <div className="keywords-col">
                      <div className="keyword-pills">
                        {result.keywords.map((kw, i) => (
                          <span
                            key={i}
                            className={`keyword-pill ${
                              !kw || !kw.match_type || kw.match_type === "not-found"
                                ? "red-pill"
                                : kw.match_type === "exact"
                                ? "green-pill"
                                : "orange-pill"
                            }`}
                            onClick={() => {
                              if (
                                (kw.match_type === "exact" || kw.match_type === "partial") &&
                                kw.url
                              ) {
                                window.open(kw.url, "_blank");
                              } else {
                                showModal(
                                  `No match found for the keyword "${kw?.keyword}".`,
                                  result.url
                                );
                              }
                            }}
                            onContextMenu={(e) => handleKeywordContextMenu(e, result, i)}
                            style={{ cursor: "pointer" }}
                          >
                            {kw?.keyword || "Unknown"}
                          </span>
                        ))}
                      </div>
                    </div>
                    {/* Time Saved Column */}
                    <div className="runtime-col">
                      <strong>Time Saved:</strong>
                      <div>{formatRuntime(timeSaved)}</div>
                      {handleDelete && (
                        <button
                          className="delete-button"
                          onClick={() => handleDeleteWithNavigation(index)}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
        ) : (
          <p>No stored results available.</p>
        )}
      </div>

      {/* CONTEXT MENU rendered using a Portal */}
      {contextMenu.visible &&
        ReactDOM.createPortal(
          <ul
            className="context-menu"
            style={{
              top: `${contextMenu.y}px`,
              left: `${contextMenu.x}px`,
            }}
          >
            <li onClick={markAsApplied}>Mark as applied</li>
            <li onClick={markAsIncorrect}>Mark as incorrect result</li>
            <li onClick={markAsCorrect}>Mark as correct result</li>
          </ul>,
          document.body
        )
      }
    </div>
  );
}

export default Tool;